import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ThumbsDown, ThumbsUp } from '@/components/icons';
import type { ReactionValue } from '@zoxo/shared';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { signInWithKakao } from '../../lib/supabase';
import { useActionGate, type GateStatus } from '../auth/action-gate';
import { OnboardingForm } from '../auth/onboarding-form';
import { useReaction, type ReactionTarget } from './use-reaction';

/**
 * 좋아요/싫어요 action bar shared by survey + article detail. Icon buttons with
 * live counts; the viewer's active reaction is highlighted. Clicking the active
 * reaction toggles it off, the opposite switches sides (state-set via useReaction).
 *
 * Reacting requires login + onboarding — an anonymous/not-onboarded viewer still
 * SEES the counts, but a click opens a login POPUP (modal) instead of an inline
 * prompt.
 */
export function ReactionBar({
  target,
  likeCount,
  dislikeCount,
  myReaction,
}: {
  target: ReactionTarget;
  likeCount: number;
  dislikeCount: number;
  myReaction: ReactionValue | null;
}) {
  const gate = useActionGate();
  const { set, clear } = useReaction(target);
  const [gateOpen, setGateOpen] = useState(false);

  const react = (value: ReactionValue) => {
    if (gate !== 'ready') {
      setGateOpen(true);
      return;
    }
    if (myReaction === value) clear.mutate(undefined);
    else set.mutate(value);
  };

  return (
    <div className="flex items-center gap-2">
      <ReactionButton
        icon={<ThumbsUp />}
        label="좋아요"
        count={likeCount}
        active={myReaction === 'like'}
        onClick={() => react('like')}
      />
      <ReactionButton
        icon={<ThumbsDown />}
        label="싫어요"
        count={dislikeCount}
        active={myReaction === 'dislike'}
        onClick={() => react('dislike')}
      />
      <GateDialog
        open={gateOpen && gate !== 'ready'}
        status={gate}
        onClose={() => setGateOpen(false)}
      />
    </div>
  );
}

/**
 * Login/onboarding popup shown when an anonymous (or not-onboarded) viewer tries
 * to react. Closes automatically once the gate becomes 'ready' (open is derived
 * from the gate status by the caller).
 */
function GateDialog({
  open,
  status,
  onClose,
}: {
  open: boolean;
  status: GateStatus;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[600] bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[601] w-[92vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface p-5 shadow-xl outline-none dark:bg-gray-950">
          <Dialog.Title className="dialog-title mb-1 font-bold text-text dark:text-gray-100">
            로그인이 필요해요
          </Dialog.Title>
          {status === 'onboarding' ? (
            <>
              <Dialog.Description className="mb-3 text-sm text-text-muted">
                회사명을 입력하면 좋아요·싫어요를 이용할 수 있어요.
              </Dialog.Description>
              <OnboardingForm />
            </>
          ) : (
            <>
              <Dialog.Description className="mb-4 text-sm text-text-muted">
                좋아요·싫어요는 로그인 후 이용할 수 있어요.
              </Dialog.Description>
              <Button
                type="button"
                fullWidth
                disabled={status === 'loading'}
                onClick={() => void signInWithKakao()}
                className="bg-[#FEE500] text-[#191919] hover:bg-[#FDD835]"
              >
                카카오로 시작하기
              </Button>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ReactionButton({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${label} ${count}`}
      title={label}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors',
        '[&_svg]:size-4',
        active
          ? 'border-primary bg-primary-soft text-primary'
          : 'border-border text-text-muted hover:bg-surface-muted',
      )}
    >
      {icon}
      <span className="tabular-nums">{count}</span>
    </button>
  );
}
